
import { defineComponent } from 'vue'
import { SEARCH_CATEGORIES } from '@/domain/constants/SearchConst'
import { capitalize } from '~/domain/helpers/formatters'
import { decodeHTMLEntities } from '~/domain/helpers'
import AppActions from '~/domain/constants/AppActions'

interface SearchResult {
  category: string
  title: string
  id: string
  link: string
}

export default defineComponent({
  name: 'CommonSearchBar',
  props: {
    inputPlaceholder: {
      type: String,
      default: '',
    },
    queryFilter: {
      type: String,
      default: '',
    },
    searchFilterItems: {
      type: Array,
      default: () =>
        Object.keys(SEARCH_CATEGORIES).map((str) => capitalize(str)),
    },
    searchTerm: {
      type: String,
      default: '',
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    inputId: {
      type: String,
      default: 'content-search-bar',
    },
    filterId: {
      type: String,
      default: 'content-search-filter',
    },
    searchIconSize: {
      type: Number,
      default: 24,
    },
    searchCanToggle: {
      type: Boolean,
      default: false,
    },
    searchBarToggled: {
      type: Boolean,
      default: true,
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    hidden: {
      type: Boolean,
      default: false,
    },
    allowEmptyQuery: {
      type: Boolean,
      default: false,
    },
    openResultsPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isSearchFocused: false,
      isToggled: false,
      showResultsSuggestions: false,
      searchResultsSuggestions: [] as SearchResult[],
      showSuggestionsLoadingSpinner: false,
      searchSuggestionsCompleted: false,
      debounce: null as any,
    }
  },
  computed: {
    term: {
      get() {
        return this.$store.state.search.term
      },
      set(val) {
        this.$store.dispatch('search/setTerm', val)
      },
    },
    searchFilter: {
      get() {
        return this.$store.state.search.filter
      },
      set(value) {
        this.$store.dispatch('search/setFilter', value)
      },
    },
    translatedPlaceholder() {
      return (
        this.inputPlaceholder ||
        (this.$t('searchSomething', { target: 'Machinations' }) as string)
      )
    },
    searchFilters() {
      return {
        selectedItem: this.searchFilter
          ? capitalize(this.searchFilter)
          : (this.searchFilterItems[0] as String),
        items: this.searchFilterItems as Array<String>,
      }
    },
    resultsSuggestionsVisible() {
      return (
        this.searchResultsSuggestions.length > 0 && this.showResultsSuggestions
      )
    },
  },
  watch: {
    searchTerm: {
      handler(newTerm) {
        this.term = newTerm
        if (this.searchCanToggle) {
          if (this.isToggled === false && this.term) {
            this.toggleSearchBar(true)
          } else if (!this.term) {
            this.toggleSearchBar(false)
          }
        }
      },
      deep: true,
    },
    searchBarToggled: {
      handler(newTerm) {
        this.isToggled = newTerm
      },
      deep: true,
    },
    queryFilter: {
      handler(newFilter) {
        this.searchFilter = (newFilter === 'designersForHire' ? 'designers' : newFilter) || '';
        this.searchResultsSuggestions = [];
        this.searchSuggestionsCompleted = false;
        this.showResultsSuggestions = false;
        if (this.isSearchFocused && this.term.trim().length > 1) {
          this.fetchSearchSuggestions();
        }
      },
    },
  },
  created() {
    this.term = this.searchTerm
    this.searchFilter = this.queryFilter || ''
    this.isToggled = this.searchBarToggled

    if (this.searchCanToggle) {
      this.toggleSearchBar(false)
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('scroll', this.handleScroll, true)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('scroll', this.handleScroll, true)
  },
  methods: {
    handleSearchEnter(term: string = '', filter: string = '') {
      if (this.searchCanToggle && this.isToggled === false) {
        this.toggleSearchBar(true)
        return
      }

      this.showResultsSuggestions = false
      clearTimeout(this.debounce)

      const searchTerm = term || this.term
      const selectedFilterLowercase = (
        filter || this.searchFilters.selectedItem
      ).toLowerCase()

      if (
        !filter &&
        !this.openResultsPage &&
        this.getCurrentPageFilter() === selectedFilterLowercase &&
        (this.term || (this.allowEmptyQuery && this.searchTerm))
      ) {
        this.updateQuery({ q: searchTerm })
        this.$emit('handle-search')
      } else if (searchTerm) {
        window.open(
          this.getCategoryResultsURL(searchTerm, selectedFilterLowercase),
          '_self'
        )
      }

      this.$store.dispatch(AppActions.TRACK_SEGMENT_EVENT, {
        context: this,
        params: {
          name: 'Global Search - Search',
          category: 'Global Search',
          action: 'Search',
          label: searchTerm,
          misc: [
            {
              type: 'location',
              value: this.searchCanToggle ? 'header' : 'search_page',
            },
            { type: 'page', value: this.$route.path },
            { type: 'filter', value: selectedFilterLowercase },
            { type: 'search term', value: searchTerm },
          ],
        },
      })
    },


    handleSearchInput() {
      if (this.term.trim().length === 0) {
        this.showResultsSuggestions = false;
      } else {
        this.fetchSearchSuggestions();
      }
    },

    handleSearchFocus() {
      this.isSearchFocused = true
      if (this.term.trim().length > 1) {
        if (this.searchResultsSuggestions.length > 0) {
          this.showResultsSuggestions = true
        } else {
          this.fetchSearchSuggestions();
        }
      }
    },

    handleSearchFocusOut() {
      this.isSearchFocused = false
    },

    handleSearchClear() {
      this.$store.dispatch('search/clearSearch')
      this.searchResultsSuggestions = []
      this.showResultsSuggestions = false
      this.handleSearchEnter()
    },

    updateQuery(params) {
      const updatedQuery = { ...this.$route.query, ...params }

      Object.keys(params).forEach((key) => {
        if (
          params[key] === null ||
          params[key] === undefined ||
          params[key]?.length === 0
        ) {
          delete updatedQuery[key]
        }
      })

      this.$router.push({ query: updatedQuery })
    },

    getCategoryResultsURL(term: string, categoryName: string) {
      const redirectURL = SEARCH_CATEGORIES[categoryName].resultsURL
      if (redirectURL) {
        return `${this.$config.nuxtBaseURL}${redirectURL}${encodeURIComponent(
          decodeHTMLEntities(term)
        )}`
      }
    },

    toggleSearchBar(value: boolean) {
      this.isToggled = value

      this.$emit('search-bar-toggled', this.isToggled)

      if (this.isToggled) {
        const searchInput = this.$refs?.searchInput as HTMLElement

        this.$nextTick(() => {
          searchInput && searchInput.focus()
        })
      }
    },

    handleFilterSelectClick() {
      this.showResultsSuggestions = false;
    },
    
    handleClickOutside(event) {
      const searchBar = this.$refs.searchBar as HTMLElement
      clearTimeout(this.debounce)

      if (searchBar && !searchBar.contains(event.target)) {
        this.showResultsSuggestions = false

        if (this.searchCanToggle && this.term.length === 0) {
          this.toggleSearchBar(false)
        }
      }
    },

    handleScroll() {
      clearTimeout(this.debounce)
      if (this.showResultsSuggestions) {
        this.showResultsSuggestions = false
      }

      const filterSelect = this.$refs.filterSelect as any;
      if (filterSelect && filterSelect.isMenuActive) {
        filterSelect.isMenuActive = false
      }
    },

    async getSearchSuggestions(
      filter: string = 'all'
    ): Promise<SearchResult[]> {
      let searchSuggestions = [] as SearchResult[]

      if (this.term.trim().length > 0) {
        searchSuggestions = (await this.$store.dispatch(
          'common/getSearchSuggestions',
          {
            context: this,
            params: {
              filter,
              term: this.term.trim(),
              count: 7,
            },
          }
        )) as SearchResult[]
      }

      return searchSuggestions
    },

    getFilteredSearchSuggestions(searchSuggestions: SearchResult[]) {
      let maxSuggestionsLeft = 7
      const sortedSuggestions: SearchResult[] = []
      const categories: { [key: string]: SearchResult[] } = {}
      const categoriesPriority = [
        {
          name: 'documentation',
          hasPriority: this.$route.path.includes('/docs'),
        },
        {
          name: 'diagrams',
          hasPriority:
            this.$route.path.includes('/community') &&
            this.$route.query?.filter !== 'designers',
        },
        {
          name: 'designers',
          hasPriority:
            this.$route.path.includes('/community') &&
            this.$route.query?.filter !== 'diagrams',
        },
      ]

      // Separate the suggestions into categories
      searchSuggestions.forEach((suggestion) => {
        if (!categories[suggestion.category]) {
          categories[suggestion.category] = []
        }
        categories[suggestion.category].push(suggestion)
      })

      // Define prioritized categories
      const prioritizedCategories: string[] = categoriesPriority
        .filter(
          (category) =>
            category.hasPriority &&
            categories[category.name] &&
            categories[category.name].length > 0
        )
        .map((category) => category.name)

      // Add one suggestion from each category if available
      for (const category in categories) {
        if (
          categories.hasOwnProperty(category) &&
          categories[category].length > 0
        ) {
          sortedSuggestions.push(categories[category].pop() as SearchResult)
          maxSuggestionsLeft--
        }
      }

      // Add suggestions from the prioritized categories (one or more)
      if (prioritizedCategories.length > 0) {
        let suggestionsAdded = true

        while (maxSuggestionsLeft > 0 && suggestionsAdded) {
          suggestionsAdded = false

          for (
            let i = 0;
            i < prioritizedCategories.length && maxSuggestionsLeft > 0;
            i++
          ) {
            const prioritizedCategory = prioritizedCategories[i]
            const prioritizedCategorySuggestions =
              categories[prioritizedCategory]

            if (
              prioritizedCategorySuggestions &&
              prioritizedCategorySuggestions.length > 0
            ) {
              sortedSuggestions.push(prioritizedCategorySuggestions.pop()!)
              maxSuggestionsLeft--
              suggestionsAdded = true
            }
          }
        }
      }

      // Add suggestions from the rest of the categories
      if (maxSuggestionsLeft > 0) {
        let suggestionsAdded = true

        while (maxSuggestionsLeft > 0 && suggestionsAdded) {
          suggestionsAdded = false

          for (
            let i = 0;
            i < Object.keys(categories).length && maxSuggestionsLeft > 0;
            i++
          ) {
            const categorySuggestions = Object.values(categories)[i]

            if (categorySuggestions && categorySuggestions.length > 0) {
              sortedSuggestions.push(categorySuggestions.pop()!)
              maxSuggestionsLeft--
              suggestionsAdded = true
            }
          }
        }
      }

      // Sort the suggestions and prioritize them
      sortedSuggestions.sort((a, b) => {
        if (prioritizedCategories.length > 0) {
          if (
            prioritizedCategories.includes(a.category) &&
            !prioritizedCategories.includes(b.category)
          ) {
            return -1
          }
          if (
            !prioritizedCategories.includes(a.category) &&
            prioritizedCategories.includes(b.category)
          ) {
            return 1
          }
        }

        if (a.category < b.category) {
          return -1
        }
        if (a.category > b.category) {
          return 1
        }

        return 0
      })

      return sortedSuggestions
    },

    getCurrentPageFilter() {
      return this.$route.query?.filter || '';
    },
    
    fetchSearchSuggestions() {
      this.searchSuggestionsCompleted = false
      this.searchResultsSuggestions = []
      clearTimeout(this.debounce)

      if (this.term.trim().length > 1) {
        this.debounce = setTimeout(async () => {
          this.showResultsSuggestions = true
          this.showSuggestionsLoadingSpinner = true

          const searchResultsSuggestions = await this.getSearchSuggestions(
            this.searchFilters.selectedItem.toLowerCase()
          )

          if (searchResultsSuggestions) {
            this.searchResultsSuggestions = this.getFilteredSearchSuggestions(
              searchResultsSuggestions
            )
          }

          this.showSuggestionsLoadingSpinner = false
          this.searchSuggestionsCompleted = true
        }, 600)
      }
    },
    
    onFilterChange(selected) {
      this.$store.dispatch('search/setFilter', selected);
      this.searchResultsSuggestions = [];
      this.searchSuggestionsCompleted = false;
      this.showResultsSuggestions = false;

      if (this.isSearchFocused && this.term.trim().length > 1) {
        this.fetchSearchSuggestions();
      }
    },

    capitalize,
  },
})
